import { uploadData } from "aws-amplify/storage";
import { fetchAuthSession } from "aws-amplify/auth";
import { IMedia } from "./models/Models";
import { StorageDb } from "./StorageDb";
import { Visibility } from "../models";
import { lookupMimeType } from "./MimeTypes";
import { v4 as uuidv4 } from "uuid";
import awsmobile from "../aws-exports";
import { PersistentJobQueue } from "./PersistentJobQueue";
import { Kernel, showFooterMessage } from "./Kernel";
import { JobDescription } from "./JobQueueInterface";
import { MessageType } from "./EventSystem";

export type MediaUploadResult = {
    succeeded: boolean;
    media?: IMedia;
    error?: string;
};

export type MediaUploadDesc = {
    jobDesc: JobDescription;
    visibility: Visibility;
    key: string;
    mimeType: string;
    name: string;
    media: IMedia;
    userData: any;
};

export async function makeS3Key(name: string): Promise<string | undefined> {
    const match = /([^.]+)(\.(\w+))?$/.exec(name);
    if (match) {
        const [, , , extension] = match;
        const { identityId } = await fetchAuthSession();
        if (identityId) {
            return `${identityId}/${uuidv4()}${extension && "."}${extension}`;
        }
    }
    return undefined;
}

/**
 * Place media access. This locally caches S3 resources and will use those if offline
 */
export class MediaLibrary extends PersistentJobQueue {
    private static _version = 3;
    private static _versionUpload = 3;
    private static _cacheExpiry = 1000 * 60 * 60 * 24 * 28;

    private _db!: StorageDb<Blob | null>;
    private _uploadDb!: StorageDb<MediaUploadDesc>;

    public constructor(kernel: Kernel) {
        super(kernel, "media-upload", 8);
        this._createDbs();
    }

    private _createDbs(): void {
        this._db = new StorageDb<Blob | null>(
            `media-library-v${MediaLibrary._version}`,
            MediaLibrary._cacheExpiry
        );

        this._uploadDb = new StorageDb<MediaUploadDesc>(
            `media-library-uploads-v${MediaLibrary._versionUpload}`,
            0
        );
    }

    public async upload(
        blob: Blob,
        visibility: Visibility,
        name: string,
        mimeType: string | undefined,
        data: any
    ): Promise<MediaUploadResult | undefined> {
        if (blob) {
            if (!mimeType || mimeType.length === 0) {
                mimeType = lookupMimeType(name);
            }
            if (name && mimeType) {
                const key = await makeS3Key(name);
                if (key) {
                    // add file to local cache so it is immediately available
                    this._db.set(key, blob);

                    const s3Object = {
                        key,
                        bucket: awsmobile.aws_user_files_s3_bucket,
                        region: awsmobile.aws_user_files_s3_bucket_region,
                    };

                    const media = {
                        id: uuidv4(),
                        mimeType: mimeType,
                        file: s3Object,
                        name: name,
                        dateAdded: new Date().toISOString(),
                    };

                    // queue for upload
                    const jobDesc = await this.startJob([key], name, { key });

                    this._uploadDb.set(key, {
                        jobDesc,
                        visibility,
                        key,
                        mimeType,
                        name,
                        media,
                        userData: data,
                    });

                    return {
                        succeeded: true,
                        media,
                    } as MediaUploadResult;
                }
            }
        }

        return undefined;
    }

    public async getCached(key: string): Promise<Blob | undefined> {
        try {
            return (await this._db.get(key)) ?? undefined;
        } catch (cachedVersion: any) {
            if (cachedVersion) {
                return cachedVersion;
            }
        }
        return undefined;
    }

    public async get(key: string): Promise<Blob | undefined> {
        // See if we have a cached version of the file.
        // We have an expired version so we then try to get the object again, if that fails
        // we just use the existing cached version
        let blob: Blob | null = null;
        let existingBlob: Blob | null = null;

        try {
            blob = await this._db.get(key);
        } catch (cachedVersion: any) {
            existingBlob = cachedVersion;
        }

        if (!blob) {
            // expired or doesn't exist so try and get a new version
            try {
                const url = `https://media.cavedb.net/${key}`;
                const response = await window.fetch(url, {
                    method: "GET",
                    mode: "cors",
                });
                if (response.status === 200) {
                    blob = await response.blob();
                    this._db.set(key, blob);
                }

                // https://docs.amplify.aws/gen1/javascript/build-a-backend/storage/storage-v5-to-v6-migration-guide/
                // const response = await Storage.get(key, { level: "public", download: true });
                // if (response.Body) {
                //     blob = await response.Body.blob();
                //     this._db.set(key, blob);
                // }
            } catch (_e: any) {
                blob = existingBlob;
            }
        }

        return blob ?? undefined;
    }

    public async getUpload(id: string): Promise<MediaUploadDesc | undefined> {
        try {
            return await this._uploadDb.get(id);
        } catch (_e: any) {
            return undefined;
        }
    }

    public async deleteUpload(upload: MediaUploadDesc): Promise<void> {
        this._uploadDb.delete(upload.key);
        this.deleteJob(upload.jobDesc.id);
    }

    // ------------------------------------------------------------------------
    // PersistentJobQueue interface
    // ------------------------------------------------------------------------

    public override async reset(): Promise<void> {
        await super.reset();
        await this._db.deleteDb();
        await this._uploadDb.deleteDb();
        this._createDbs();
    }

    protected override async syncEntry(key: string): Promise<void> {
        const desc = await this._uploadDb.get(key);
        const blob = await this.get(desc.key);
        if (blob) {
            const file = new File([blob], desc.name);

            const op = uploadData({
                key: desc.key,
                data: file,
                options: {
                    accessLevel: desc.visibility === "public" ? "guest" : "private",
                    contentType: desc.mimeType,
                    onProgress: ({ transferredBytes, totalBytes }) => {
                        if (totalBytes) {
                            console.log(
                                `Uploading media: ${((transferredBytes / totalBytes) * 100).toFixed(0)}%`
                            );
                            showFooterMessage(
                                `Uploading media: ${((transferredBytes / totalBytes) * 100).toFixed(0)}%`,
                                MessageType.Info,
                                2000
                            );
                        }
                    },
                },
            });
            await op.result;
            this._uploadDb.delete(key);
        }
    }

    protected override async removeEntry(key: string): Promise<void> {
        this._uploadDb.delete(key);
    }

    protected override async jobCompleted(desc: JobDescription): Promise<void> {
        // we don't need to keep track of what we uploaded
        this._deleteJobCache(desc);
    }
}
